import { useState, useEffect, useMemo } from "react";
import { useApp } from "../../store.jsx";
import { Icon, Avatar, EmptyState } from "../../components/Common.jsx";
import {
  listTeamTasks,
  listTeam,
  deleteTask,
} from "../../lib/api";
import { AssignTaskModal } from "../../components/Modals.jsx";

export default function AssignTasks() {
  const { toast, openModal, closeModal } = useApp();
  const [tasks, setTasks] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [priority, setPriority] = useState("all");
  const [expandedDepts, setExpandedDepts] = useState(() => new Set());
  const [expandedEmps, setExpandedEmps] = useState(() => new Set());

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ tasks }, { departments }] = await Promise.all([
        listTeamTasks(),
        // scope: "company" → managers get the full company directory (same as the
        // owner), so the stats, grouping, and AssignTaskModal assignee list match
        // the company-wide task board returned by listTeamTasks().
        listTeam({ scope: "company", groupBy: "department" }).catch(() => ({ departments: [] })),
      ]);
      const rows = (tasks || []).sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      setTasks(rows);
      setDirectory(departments || []);
    } catch (e) {
      toast(e.message || "Could not load tasks", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []); // eslint-disable-line

  const openAssign = () =>
    openModal(
      <AssignTaskModal
        departments={directory}
        onClose={closeModal}
        onSubmit={() => {
          toast("Task assigned", "success");
          refresh();
        }}
      />,
      "md",
    );

  const openAssignFor = (userId) =>
    openModal(
      <AssignTaskModal
        departments={directory}
        onClose={closeModal}
        onSubmit={() => {
          toast("Task assigned", "success");
          refresh();
        }}
        initialUserId={userId}
      />,
      "md",
    );

  const remove = async (id) => {
    try {
      await deleteTask(id);
      toast("Task deleted", "success");
      refresh();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  // ---- derived ----
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - 7);
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const todayStr = now.toISOString().slice(0, 10);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tasks.filter((t) => {
      if (q) {
        const hay = [
          t.title,
          t.description,
          t.user?.name,
          t.user?.email,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (priority !== "all" && (t.priority || "low") !== priority) return false;
      const created = new Date(t.createdAt);
      if (filter === "today") return t.createdAt?.slice(0, 10) === todayStr;
      if (filter === "week") return created >= startOfWeek;
      if (filter === "month") return created >= startOfMonth;
      if (filter === "assigned") return t.source === "assigned";
      if (filter === "self") return t.source === "self_added";
      return true;
    });
  }, [tasks, search, filter, priority]); // eslint-disable-line

  const totalEmployees = useMemo(
    () => directory.reduce((s, d) => s + d.users.length, 0),
    [directory],
  );

  const stats = useMemo(() => {
    const active = tasks.filter((t) => t.status === "active");
    const thisWeek = tasks.filter((t) => new Date(t.createdAt) >= startOfWeek);
    const selfAdded = tasks.filter((t) => t.source === "self_added");
    return {
      total: active.length,
      employees: totalEmployees,
      thisWeek: thisWeek.length,
      self: selfAdded.length,
    };
  }, [tasks, totalEmployees]); // eslint-disable-line

  const grouped = useMemo(() => {
    const map = {};
    for (const task of filtered) {
      const dept = task.user?.department || "No Department";
      const uid = task.user?.id || "unknown";
      if (!map[dept]) map[dept] = { name: dept, employees: {} };
      if (!map[dept].employees[uid]) {
        map[dept].employees[uid] = { user: task.user, tasks: [] };
      }
      map[dept].employees[uid].tasks.push(task);
    }
    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, dept]) => ({
        name: dept.name,
        employees: Object.values(dept.employees)
          .sort((a, b) => (a.user?.name || "").localeCompare(b.user?.name || "")),
        taskCount: Object.values(dept.employees).reduce((s, e) => s + e.tasks.length, 0),
        employeeCount: Object.values(dept.employees).length,
      }));
  }, [filtered]);

  const toggleDept = (name) =>
    setExpandedDepts((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const toggleEmp = (id) =>
    setExpandedEmps((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // ---- styles ----
  const statCard = {
    flex: 1,
    minWidth: 160,
    padding: 14,
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--surface-2)",
  };
  const statLabel = {
    fontSize: 11.5,
    fontWeight: 600,
    color: "var(--text-muted)",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  };
  const statValue = { fontSize: 22, fontWeight: 700, marginTop: 6 };

  const renderRow = (t) => {
    const accent =
      t.source === "assigned"
        ? "var(--primary, #6366f1)"
        : "var(--success, #22c55e)";
    return (
      <div
        key={t.id}
        style={{
          padding: 12,
          paddingLeft: 14,
          borderRadius: 10,
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderLeft: `3px solid ${accent}`,
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          transition: "border-color .15s ease",
        }}
      >
        <Avatar
          name={t.user?.name || "?"}
          size="sm"
          seed={(t.user?.id || "").length}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 13.5 }}>
              {t.user?.name || "Unknown"}
            </span>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {t.user?.email}
            </span>
            {t.source === "assigned" ? (
              <span
                className="badge badge-muted"
                style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
              >
                <Icon name="briefcase" size={11} /> assigned
              </span>
            ) : (
              <span className="badge badge-muted">self</span>
            )}
            {t.priority && t.priority !== "low" && (
              <span
                className="badge"
                style={{
                  background: t.priority === "high" ? "rgba(239,68,68,0.15)" : "rgba(245,158,11,0.15)",
                  color: t.priority === "high" ? "#ef4444" : "#f59e0b",
                  fontSize: 10, fontWeight: 700,
                }}
              >
                {t.priority === "high" ? "🔴" : "🟡"} {t.priority}
              </span>
            )}
            {t.reminderCount > 0 && (
              <span className="badge badge-muted" style={{ fontSize: 10 }}>
                {t.reminderCount}/{t.maxReminders} reminders
              </span>
            )}
            <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--text-muted)" }}>
              {new Date(t.createdAt).toLocaleDateString()}
            </span>
          </div>
          <div
            style={{
              marginTop: 6,
              fontSize: 14,
              fontWeight: 600,
              color: "var(--text)",
            }}
          >
            {t.title}
          </div>
          {t.description && (
            <div
              style={{
                marginTop: 4,
                fontSize: 12.5,
                color: "var(--text-muted)",
                lineHeight: 1.45,
              }}
            >
              {t.description}
            </div>
          )}
        </div>
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <button
            className="icon-btn"
            title="Delete task"
            onClick={() =>
              openModal(
                <div style={{ padding: 16 }}>
                  <h3 style={{ fontSize: 16 }}>Delete task?</h3>
                  <p style={{ color: "var(--text-muted)", fontSize: 13, margin: "8px 0 16px" }}>
                    Are you sure you want to delete <strong>"{t.title}"</strong>? This cannot be undone.
                  </p>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
                    <button className="btn btn-danger" onClick={() => { closeModal(); remove(t.id); }}>
                      <Icon name="trash-2" size={14} /> Delete
                    </button>
                  </div>
                </div>,
                "sm",
              )
            }
          >
            <Icon name="trash" size={14} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Assign Tasks</h1>
          <p className="page-sub">
            All tasks across your team. Assign new work or remove.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openAssign}>
          <Icon name="plus" size={16} /> Assign Task
        </button>
      </div>

      {/* Stats strip */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={statCard}>
          <div style={statLabel}>Active tasks</div>
          <div style={statValue}>{stats.total}</div>
        </div>
        <div style={statCard}>
          <div style={statLabel}>Team members</div>
          <div style={{ ...statValue, color: "var(--primary, #6366f1)" }}>
            {stats.employees}
          </div>
        </div>
        <div style={statCard}>
          <div style={statLabel}>Created this week</div>
          <div style={{ ...statValue, color: "var(--warning, #f59e0b)" }}>
            {stats.thisWeek}
          </div>
        </div>
        <div style={statCard}>
          <div style={statLabel}>Self-added by team</div>
          <div style={{ ...statValue, color: "var(--success, #22c55e)" }}>
            {stats.self}
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="table-toolbar" style={{ marginBottom: 14 }}>
        <div className="toolbar-left">
          <div className="toolbar-search">
            <Icon name="search" size={14} />
            <input
              type="text"
              placeholder="Search by task, employee name, or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {[
            ["all", "All"],
            ["today", "Today"],
            ["week", "This week"],
            ["month", "This month"],
            ["assigned", "Assigned"],
            ["self", "Self-added"],
          ].map(([k, l]) => (
            <button
              key={k}
              className={`filter-chip ${filter === k ? "active" : ""}`}
              onClick={() => setFilter(k)}
            >
              {l}
            </button>
          ))}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              marginLeft: 4,
            }}
          >
            <Icon name="flag" size={13} />
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              style={{
                padding: "6px 10px",
                borderRadius: 6,
                border: "1px solid var(--border)",
                background: "var(--surface)",
                color: "var(--text)",
                fontSize: 12.5,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <option value="all">All priorities</option>
              <option value="high">🔴 High</option>
              <option value="medium">🟡 Medium</option>
              <option value="low">⚪ Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Task list grouped by department → employee */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "12px 16px",
            borderBottom: "1px solid var(--border)",
            background: "var(--surface-2)",
          }}
        >
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: "rgba(99,102,241,.15)",
              color: "var(--primary, #6366f1)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon name="check-square" size={15} />
          </div>
          <span style={{ fontWeight: 600, fontSize: 14 }}>Team tasks</span>
          <span className="badge badge-muted" style={{ fontSize: 11 }}>
            {filtered.length}
          </span>
        </div>

        <div style={{ padding: 14 }}>
          {loading ? (
            <div
              style={{
                color: "var(--text-muted)",
                fontSize: 13,
                padding: "24px 4px",
                textAlign: "center",
              }}
            >
              Loading team tasks…
            </div>
          ) : totalEmployees === 0 ? (
            <EmptyState
              title="No team members"
              desc="You don't have any direct reports yet."
              icon="users"
            />
          ) : filtered.length === 0 ? (
            <EmptyState
              title={
                tasks.length === 0
                  ? "No tasks yet"
                  : "No tasks match your filters"
              }
              desc={
                tasks.length === 0
                  ? 'Click "Assign Task" to give your first task.'
                  : "Try a different search or filter."
              }
              icon="check-square"
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {grouped.map((dept) => {
                const deptOpen = expandedDepts.has(dept.name);
                return (
                  <div key={dept.name} className="card" style={{ padding: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "10px 14px",
                        cursor: "pointer",
                        borderBottom: deptOpen ? "1px solid var(--border)" : "none",
                        background: deptOpen ? "var(--surface-2)" : "transparent",
                        borderRadius: "10px 10px 0 0",
                      }}
                      onClick={() => toggleDept(dept.name)}
                    >
                      <Icon name={deptOpen ? "chevron-down" : "chevron-right"} size={14} />
                      <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{dept.name}</span>
                      <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        {dept.taskCount} tasks · {dept.employeeCount} employees
                      </span>
                    </div>
                    {deptOpen && (
                      <div style={{ padding: "4px 14px 14px" }}>
                        {dept.employees.map((emp) => {
                          const empOpen = expandedEmps.has(emp.user?.id);
                          return (
                            <div key={emp.user?.id || "?"} style={{ marginTop: 8 }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                  padding: "8px 10px",
                                  cursor: "pointer",
                                  borderRadius: 8,
                                  background: empOpen ? "var(--surface-2)" : "transparent",
                                }}
                                onClick={() => toggleEmp(emp.user?.id)}
                              >
                                <Icon name={empOpen ? "chevron-down" : "chevron-right"} size={12} />
                                <Avatar
                                  name={emp.user?.name || "?"}
                                  size="sm"
                                  seed={(emp.user?.id || "").length}
                                />
                                <span style={{ flex: 1, fontWeight: 500, fontSize: 13 }}>
                                  {emp.user?.name || "Unknown"}
                                </span>
                                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                  {emp.tasks.length} task{emp.tasks.length !== 1 ? "s" : ""}
                                </span>
                                <button
                                  className="icon-btn"
                                  title="Assign task"
                                  style={{ width: 24, height: 24 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openAssignFor(emp.user?.id);
                                  }}
                                >
                                  <Icon name="plus" size={12} />
                                </button>
                              </div>
                              {empOpen && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 6,
                                    marginTop: 6,
                                    paddingLeft: 32,
                                  }}
                                >
                                  {emp.tasks.map(renderRow)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
